// Home.jsx - Complete NexaLife Care Training Landing Page
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CiCircleChevDown, CiCircleChevUp } from "react-icons/ci";
import logo from "../assets/logo.png";
import bgImg from "../assets/care-hero-bg.png";
import heroImg from "../assets/care-hero-image.png";
import keyCourseBg from "../assets/care-key-course-bg.png";
import cardBgImg from "../assets/care-card-bg.png";
import viewCourseBg from "../assets/view-course-bg.svg";
import viewImgBg from "../assets/view-course-img-bg.svg";
import viewImg from "../assets/care-view-course-img.png";

import icon1 from "../assets/healthicons_i-training-class-24px.svg";
import icon2 from "../assets/ic_sharp-privacy-tip.svg";
import icon3 from "../assets/bxs_certification.svg";
import icon4 from "../assets/eos-icons_self-healing.svg";
import icon5 from "../assets/tabler_alarm-average.svg";

const courseOutline = [
	"What Is HIPAA and Why It Matters",
	"The HIPAA Privacy Rule",
	"The HIPAA Security Rule",
	"Breaches and What To Do If Something Goes Wrong",
	"Business Associates and Shared Responsibility",
	"Daily Compliance in Real-World Situations",
];
 
const keyCourseDetailsData = [
	{ id: 1, text: "HIPAA compliance training tailored for providers", icon: icon1 },
	{ id: 2, text: "Covers privacy, data handling protocols, and cultural competence", icon: icon2 },
	{ id: 3, text: "Certification issued upon completion", icon: icon3 },
	{ id: 4, text: "Self-paced format", icon: icon4 },
	{ id: 5, text: "Average completion time: 2 hours", icon: icon5 },
];

const Home = () => {
	const navigate = useNavigate();
	const [showOutline, setShowOutline] = useState(false);

	// Always scroll to top on mount
	useEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	const handleGetStarted = () => {
		navigate('/login');
	};

	return (
		<div className='relative min-h-screen'>
			{/* Navbar */}
			<header className='w-full fixed top-0 z-50 backdrop-blur-md bg-[#F8FAFC4D]/50 px-4 border-b border-[#C2C2C2]'>
				<div className='max-w-[1440px] mx-auto lg:w-[86.5%] w-full lg:py-2.5 py-1.5 flex items-center justify-between'>
					<div className='flex-shrink-0'>
						<img
							src={logo}
							alt='NexaLife Care Logo'
							className='h-20 lg:h-24 object-cover'
						/>
					</div>
					<button
						onClick={handleGetStarted}
						className='bg-[#01ABCA] transition-all duration-500 border-2 border-[#01ABCA] hover:text-[#01ABCA] hover:bg-white rounded-lg text-white lg:text-base/6 text-sm/5 font-bold md:py-[14px] py-2 md:px-[26.5px] px-5 cursor-pointer'>
						Get Started
					</button>
				</div>
			</header>

			{/* Hero Section */}
			<div
				className='bg-no-repeat bg-left-top overflow-hidden'
				style={{ backgroundImage: `url(${bgImg})` }}>
				<div className='max-w-[1440px] mx-auto w-[86.5%] lg:min-h-screen lg:space-y-0 space-y-7'>
					<div className='space-y-5 max-w-[630px] w-full lg:pt-[230px] pt-[110px] lg:pb-[130px] pb-0 lg:text-left text-center'>
						<h1 className='text-[#00434E] lg:text-[70px]/[79px] text-[32px]/[36px] font-bold'>
							NexaLife Care HIPAA Compliance Training
						</h1>
						<p className='text-[#202020] font-bold lg:text-2xl/8 text-lg/6'>
							Required Certification for Provider Access
						</p>
						<p className='text-[#5A5A5A] lg:text-lg/8 text-sm/5 font-semibold'>
							Participation in the NexaLife Care referral system requires
							documented HIPAA compliance. This training provides the regulatory
							foundation for handling protected health information(PHI) within the
							system.
						</p>
						<button
							onClick={handleGetStarted}
							className='block w-fit lg:mx-0 mx-auto bg-[#01ABCA] transition-all duration-500 border-2 border-[#01ABCA] hover:text-[#01ABCA] hover:bg-white rounded-lg text-white lg:text-base/6 text-sm/5 font-bold md:py-[14px] py-2 md:px-[26.5px] px-5 cursor-pointer'>
							Begin Certification
						</button>
					</div>
					<div className='lg:hidden flex justify-center items-center'>
						<img src={heroImg} alt='' />
					</div>
				</div>
			</div>

			{/* Key Course Details Section */}
			<div className='bg-[#00434E]'>
				<div
					className='bg-no-repeat bg-bottom'
					style={{ backgroundImage: `url(${keyCourseBg})` }}>
					<div className='max-w-[1440px] mx-auto w-[86.5%] md:space-y-16 space-y-10'>
						<div className='pt-10 text-[#E6F9FC] text-xl/6 font-bold space-y-4'>
							<h1 className='lg:text-[70px]/[79px] text-[36px]/[40px] font-bold'>
								Key Course Details
							</h1>
							<p>
								Access to the referral system is granted only after certification
								is verified.
							</p>
						</div>
						<ul className='pb-32 flex justify-center gap-6 flex-wrap'>
							{keyCourseDetailsData.map((item) => (
								<li
									key={item.id}
									className='lg:w-[384px] sm:w-[300px] w-full rounded-[15px] bg-[#E6F9FC] shadow-[0px_4px_4px_0px_rgba(62,75,77,0.16),0px_3px_4px_0px_rgba(223,223,223,0.14)]'>
									<div
										className='rounded-[15px] bg-no-repeat bg-top flex flex-col items-center justify-center text-center px-11 py-[18px] gap-y-6'
										style={{ backgroundImage: `url(${cardBgImg})` }}>
										<span className='w-[99px] h-[80px] rounded-[10px] border-[3px] border-[#01BEE0] flex items-center justify-center'>
											<img src={item.icon} alt='' className='w-16 h-16' />
										</span>
										<p className='text-xl/6 font-semibold'>{item.text}</p>
									</div>
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>

			{/* View Course Details Section */}
			<div className='bg-[#F2FCFD]'>
				<div
					className='bg-no-repeat bg-right-bottom md:py-20 py-16 space-y-16'
					style={{ backgroundImage: `url(${viewImgBg})` }}>
					<div className='bg-[#00434E] rounded-3xl max-w-[1100px] w-[90%] mx-auto max-h-[340px] overflow-hidden h-full'>
						<div
							className='bg-repeat-x bg-center bg-contain md:py-3 py-4'
							style={{ backgroundImage: `url(${viewCourseBg})` }}>
							<img
								src={viewImg}
								alt=''
								className='aspect-video max-w-[724px] w-full mx-auto h-full'
							/>
						</div>
					</div>

					<div className='max-w-3xl lg:w-full w-[90%] mx-auto lg:space-y-10 space-y-8 pb-20'>
						<h1 className='text-[#101828] lg:text-4xl/10 text-xl/6 underline font-semibold flex items-center gap-x-3'>
							<button
								className='transition-in-out duration-300 ease-linear flex items-center justify-center font-bold'
								title='View Course'
								onClick={() => setShowOutline((prev) => !prev)}>
								{showOutline ? (
									<CiCircleChevUp
										className='text-4xl'
										color='#005565'
										strokeWidth='1px'
									/>
								) : (
									<CiCircleChevDown
										className='text-4xl'
										color='#005565'
										strokeWidth='1px'
									/>
								)}
							</button>
							View Full Course Outline
						</h1>

						{showOutline && (
							<ul className='w-[90%] mx-auto space-y-4 text-[#667085] text-base/6 font-semibold list-disc'>
								{courseOutline.map((item, index) => (
									<li key={index}>{item}</li>
								))}
							</ul>
						)}
						<button
							onClick={handleGetStarted}
							className='block w-fit bg-[#01ABCA] transition-all duration-500 border-2 border-[#01ABCA] hover:text-[#01ABCA] hover:bg-white rounded-lg text-white text-base/6 font-bold md:py-[14px] py-2.5 md:px-[26.5px] px-5 cursor-pointer'>
							Begin Certification
						</button>
					</div>
				</div>
			</div>

			{/* Footer */}
			<footer className='w-full bg-[#CFDDDF] text-[#00434E] text-xl/6 font-semibold py-6'>
				<div className='max-w-[1440px] mx-auto w-[86.5%] py-2.5 flex flex-wrap sm:gap-0 gap-y-6 items-center justify-between'>
					<p>
						Contact Support:{' '}
						<a className='hover:underline' href='mailto:support@nexalifecare.com'>
							support@nexalifecare.com
						</a>
					</p>
					<p>
						Phone Number:{' '}
						<a className='hover:underline' href='tel:+1 (888) 569-9764'>
							+1 (888) 569-9764
						</a>
					</p>
				</div>
			</footer>
		</div>
	);
};

export default Home;